import {DataFetcher} from '@aboutyou/data-fetcher';
import {ProductDetailsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  RADIUS,
  SPACING,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {ProductDetailsModalContent} from './ProductDetailsModalContent';
import {useCheckoutProductDetails} from './useCheckoutProductDetails';

interface ProductDetailsModalProps {
  open: boolean;
  onClose: () => void;
  productId: number;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
}

const ProductDetailsPopperView: React.FC<
  ProductDetailsSection & {
    LayoutComponent: ContentPopperProps['LayoutComponent'];
    open: boolean;
    onClose: () => void;
  }
> = ({articleDetails, LayoutComponent, open, onClose}) => {
  const {t} = useTranslation();

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      open={open}
      onClose={onClose}
      headerContent={t('Product Details')}
      bodyContent={
        <ProductDetailsModalContent articleDetails={articleDetails} />
      }
      footerContent={
        <CloseButton onClick={onClose} data-testid="product-details-close-btn">
          {t('Close')}
        </CloseButton>
      }
      renderInPortal
      data-testid="product-details-modal"
    />
  );
};

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  open,
  onClose,
  productId,
  LayoutComponent,
}) => {
  const request = useCheckoutProductDetails(productId, open);

  if (!open) {
    return null;
  }

  return (
    <DataFetcher
      request={request}
      View={ProductDetailsPopperView}
      Loading={() => null}
      Error={null}
      viewProps={{LayoutComponent, open, onClose}}
    />
  );
};

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${SPACING['Y.Xl4']};
  background: ${COLORS.White};
  border: 1px solid ${COLORS['Grey.Shade300']};
  border-radius: ${RADIUS.Sm};
  cursor: pointer;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS.Black};
  padding: 0 ${SPACING['X.Xl']};

  &:hover {
    background: ${COLORS['Grey.Shade100']};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.Black};
    outline-offset: 2px;
  }

  &:active {
    background: ${COLORS['Grey.Shade200']};
  }
`;
